/**
 * What this symbol has working at the broker, and a way to pull it.
 *
 * An order placed from the ticket is real and resting; without this it would
 * leave the screen the moment it was sent, and the only way to see or cancel it
 * would be the Bots page. Read straight from the broker's own order list, so it
 * shows orders placed anywhere — this ticket, another device, or a bot.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/UI';
import { cancelBrokerOrder, getBrokerOrders, type BrokerOrderView } from '../../lib/bots';

const OPEN = new Set(['SUBMITTED', 'RECEIVED', 'QUEUED', 'PARTIAL']);
const mono = { fontFamily: 'var(--tk-mono)', fontVariantNumeric: 'tabular-nums' as const };

export const WorkingOrders = ({ sym }: { sym: string }) => {
  const [orders, setOrders] = useState<BrokerOrderView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (fresh = false) => {
    try {
      const r = await getBrokerOrders(fresh);
      setOrders(r.orders);
      setError(null);
    } catch {
      // A signed-in user without broker access simply has no orders to show.
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 6000);
    return () => clearInterval(timer);
  }, [load]);

  const mine = (orders ?? []).filter((o) => o.symbol.toUpperCase() === sym.toUpperCase() && OPEN.has(o.status));
  if (!orders || mine.length === 0) return null;

  const pull = async (order: BrokerOrderView) => {
    setBusy(order.brokerOrderId);
    setError(null);
    try {
      await cancelBrokerOrder(order.brokerOrderId, order.exchangeOrderId || undefined);
      await load(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card padding={20}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Working orders</h3>
        <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', letterSpacing: '0.06em', ...mono }}>{mine.length} on {sym}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {mine.map((o) => {
          const color = o.side === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)';
          return (
            <div
              key={o.brokerOrderId || o.exchangeOrderId}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 'var(--tk-radius-sm)', background: 'var(--tk-surface-2)' }}
            >
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', color, width: 32 }}>{o.side}</span>
              <span style={{ fontSize: 14, fontWeight: 600, ...mono }}>{o.type === 'MARKET' ? 'mkt' : o.price.toFixed(2)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', ...mono }}>
                {o.executed > 0 ? `${o.executed.toLocaleString()}/${o.quantity.toLocaleString()}` : o.quantity.toLocaleString()} sh
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--tk-ink-3)', letterSpacing: '0.05em', ...mono }}>{o.status}</span>
              <button
                onClick={() => void pull(o)}
                disabled={busy === o.brokerOrderId}
                title="Cancel this order at the broker"
                style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, border: '1px solid var(--tk-line)', borderRadius: 6, background: 'transparent', color: 'var(--tk-ink-2)', cursor: busy ? 'default' : 'pointer' }}
              >
                {busy === o.brokerOrderId ? '…' : 'Cancel'}
              </button>
            </div>
          );
        })}
      </div>
      {error && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--tk-down)' }}>{error}</div>}
    </Card>
  );
};
